import { ref, computed } from 'vue';
import useCurrentTime from '~/Composables/useCurrentTime';

const useEventmapPathFinding = (eventmap, eventmapSVG, routeStart, routeEnd) => {
  const paths = ref([]);
  const errors = ref(null);

  const manhattanDistance = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  const euclideanDistance = (a, b) => Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);

  const key = (point) => `${point.x}_${point.y}`;
  const dekey = (k) => {
    const [x, y] = k.split('_').map(Number);
    return { x, y };
  };

  const reconstructPath = (cameFrom, current) => {
    const path = [];
    let currentNode = current;
    while (currentNode) {
      path.unshift(dekey(currentNode));
      currentNode = cameFrom.get(currentNode);
    }
    return path;
  };

  const aStar = (graph, start, goal, metric = manhattanDistance) => {
    if (!graph || !start || !goal) {
      errors.value = 'Invalid graph, start, or goal.';
      return null;
    }

    const nodes = graph.flatMap((node) => [
      { x: node.x1, y: node.y1 },
      { x: node.x2, y: node.y2 },
    ]);

    const findClosestNode = (point) => nodes.reduce((closest, node) => (!closest || euclideanDistance(point, node) < euclideanDistance(point, closest) ? node : closest), null);

    const startNode = findClosestNode(start);
    const goalNode = findClosestNode(goal);

    if (!startNode || !goalNode) {
      errors.value = 'Start or goal node not found.';
      return null;
    }

    const queue = new Set([key(startNode)]);
    const cameFrom = new Map();
    const gScore = new Map(nodes.map((node) => [key(node), Infinity]));
    const fScore = new Map(nodes.map((node) => [key(node), Infinity]));

    gScore.set(key(startNode), 0);
    fScore.set(key(startNode), metric(startNode, goalNode));

    let iterationCount = 0;
    const maxIterations = 500;
    while (queue.size > 0) {
      if (iterationCount > maxIterations) {
        errors.value = 'Pathfinding exceeded maximum iterations.';
        return null;
      }
      iterationCount += 1;
      const current = [...queue].reduce((lowest, nodeKey) => (fScore.get(nodeKey) < fScore.get(lowest) ? nodeKey : lowest), [...queue][0]);

      if (current === key(goalNode)) return reconstructPath(cameFrom, current);

      queue.delete(current);

      graph.forEach((path) => {
        const neighbors = [];
        if (path.x1 === dekey(current).x && path.y1 === dekey(current).y) neighbors.push({ x: path.x2, y: path.y2 });
        if (path.x2 === dekey(current).x && path.y2 === dekey(current).y) neighbors.push({ x: path.x1, y: path.y1 });

        neighbors.forEach((neighbor) => {
          const tentativeGScore = gScore.get(current) + euclideanDistance(dekey(current), neighbor);

          if (tentativeGScore < gScore.get(key(neighbor))) {
            cameFrom.set(key(neighbor), current);
            gScore.set(key(neighbor), tentativeGScore);
            fScore.set(key(neighbor), tentativeGScore + metric(neighbor, goalNode));
            queue.add(key(neighbor));
          }
        });
      });
    }

    errors.value = 'No path found.';
    return null;
  };

  const calculateBounds = () => {
    if (!paths.value?.length) return null;

    return paths.value.reduce(
      (acc, point) => ({
        minX: Math.min(acc.minX, point.x),
        minY: Math.min(acc.minY, point.y),
        maxX: Math.max(acc.maxX, point.x),
        maxY: Math.max(acc.maxY, point.y),
      }),
      {
        minX: routeStart?.value?.centre_x || 0,
        minY: routeStart?.value?.centre_y || 0,
        maxX: routeStart?.value?.centre_x || 0,
        maxY: routeStart?.value?.centre_y || 0,
      },
    );
  };

  const plotRoute = () => {
    if (!routeStart?.value || !routeEnd?.value) {
      errors.value = 'Route start or end is not defined.';
      return;
    }

    paths.value = aStar(
      eventmap?.value?.paths || [],
      { x: routeStart.value.centre_x, y: routeStart.value.centre_y },
      { x: routeEnd.value.centre_x, y: routeEnd.value.centre_y },
    );

    if (!paths.value?.length) {
      errors.value = 'No valid path found.';
      return;
    }

    const bounds = calculateBounds();

    setTimeout(() => {
      if (bounds && eventmapSVG?.value) {
        eventmapSVG.value.zoomToExtents(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY, 2);
      }
    }, 200);
  };

  const routeDistance = computed(() => {
    if (!paths.value?.length) return 0;

    return Math.round(
      paths.value.reduce((distance, point, index, path) => (index === 0 ? distance : distance + euclideanDistance(path[index - 1], point)), 0)
      / (eventmap?.value?.rounding_factor || 1),
    );
  });

  const routeDistanceFormatted = computed(() => `${routeDistance.value} ${eventmap?.value?.rounded_units_label || ''}`);

  const routeJourneyTime = computed(() => Math.max(1, Math.floor((routeDistance.value / 100) * 1.8)));

  const { currentTime } = useCurrentTime();

  const routeEta = computed(() => {
    const eta = new Date(currentTime.value.getTime() + routeJourneyTime.value * 60000);
    return new Intl.DateTimeFormat(document.documentElement.lang, { timeStyle: 'short' }).format(eta);
  });

  return {
    paths,
    errors,
    routeDistance,
    routeDistanceFormatted,
    routeJourneyTime,
    routeEta,
    plotRoute,
    aStar,
  };
};

export default useEventmapPathFinding;
